import React from "react";
import { FiLock, FiLogOut, FiArrowRight, FiShield } from "react-icons/fi";
import twynLogo from "./assets/twyn-logo.png";

// ---------------------------------------------------------------------------
// 1) LOADING SCREEN
// ---------------------------------------------------------------------------
export function LoadingScreen() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100vw",
        height: "100vh",
        background: "#122035", // 9F Brand Dark Blue Background
        fontFamily: "'Century Gothic', sans-serif",
        color: "#ffffff",
      }}
    >
      <div style={{ position: "relative", marginBottom: "2rem" }}>
        <img
          src={twynLogo}
          alt="9F Twyn Logo"
          style={{
            width: "120px",
            height: "120px",
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
          }}
        />
        {/* Pulsing loading glow ring */}
        <div
          style={{
            position: "absolute",
            top: "-10px",
            left: "-10px",
            right: "-10px",
            bottom: "-10px",
            border: "3px solid #22D4F0",
            borderRadius: "24px",
            animation: "pulse 2s infinite ease-in-out",
            opacity: 0.8,
            boxShadow: "0 0 20px rgba(34, 212, 240, 0.4)",
          }}
        />
      </div>

      <style>{`
        @keyframes pulse {
          0% { transform: scale(0.95); opacity: 0.3; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(0.95); opacity: 0.3; }
        }
      `}</style>

      <h2
        style={{
          fontFamily: "Avenir, sans-serif",
          fontWeight: 700,
          letterSpacing: "0.15em",
          fontSize: "1.2rem",
          color: "#22D4F0",
          textTransform: "uppercase",
          margin: "0 0 0.5rem 0",
        }}
      >
        Initializing Portal
      </h2>
      <p style={{ margin: 0, opacity: 0.6, fontSize: "0.95rem" }}>
        Securing the workspace, please wait...
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2) LOGIN GATEWAY
// ---------------------------------------------------------------------------
export function LoginGateway({ loginWithRedirect }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100vw",
        height: "100vh",
        background: "radial-gradient(circle at center, #1E344F 0%, #122035 100%)", // Rich HSL-tailored gradient
        fontFamily: "'Century Gothic', sans-serif",
        color: "#ffffff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "480px",
          padding: "3.5rem 3rem",
          background: "rgba(18, 32, 53, 0.85)", // Glassmorphism
          borderRadius: "24px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          textAlign: "center",
          transform: "translateY(-10px)",
          transition: "transform 0.3s ease",
        }}
      >
        <img
          src={twynLogo}
          alt="9F Twyn Logo"
          style={{
            width: "100px",
            height: "100px",
            borderRadius: "16px",
            marginBottom: "1.5rem",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.25)",
          }}
        />

        <h1
          style={{
            fontFamily: "Avenir, sans-serif",
            fontWeight: 700,
            fontSize: "1.8rem",
            letterSpacing: "0.05em",
            margin: "0 0 0.5rem 0",
            color: "#ffffff",
          }}
        >
          TWIN
        </h1>

        <div
          style={{
            fontFamily: "Avenir, sans-serif",
            fontWeight: 500,
            fontSize: "0.85rem",
            color: "#22D4F0",
            letterSpacing: "0.2em",
            marginBottom: "2rem",
            textTransform: "uppercase",
          }}
        >
          By 9 Foundations
        </div>

        <p
          style={{
            fontSize: "1rem",
            lineHeight: "1.6",
            color: "#B2CCEE", // Soft light blue body text
            margin: "0 0 2.5rem 0",
          }}
        >
          Please authenticate
        </p>

        <button
          onClick={() => loginWithRedirect({
            appState: { returnTo: window.location.pathname }
          })}
          style={{
            width: "100%",
            padding: "1.1rem",
            background: "linear-gradient(135deg, #0A74D6 0%, #004174 100%)", // 9F Brand blues
            border: "none",
            borderRadius: "12px",
            color: "#ffffff",
            fontSize: "1.05rem",
            fontWeight: "bold",
            cursor: "pointer",
            boxShadow: "0 6px 20px rgba(10, 116, 214, 0.3)",
            transition: "all 0.2s ease-in-out",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.6rem",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 8px 25px rgba(10, 116, 214, 0.45)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(10, 116, 214, 0.3)";
          }}
        >
          <FiShield style={{ fontSize: "1.2rem" }} />
          Sign In
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3) ACCESS DENIED SCREEN
// ---------------------------------------------------------------------------
export function AccessDeniedScreen({ user, requestedClient, allowedClients, logout }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100vw",
        height: "100vh",
        background: "radial-gradient(circle at center, #1E344F 0%, #122035 100%)",
        fontFamily: "'Century Gothic', sans-serif",
        color: "#ffffff",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "500px",
          padding: "3.5rem 3rem",
          background: "rgba(18, 32, 53, 0.85)",
          borderRadius: "24px",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "80px",
            height: "80px",
            background: "rgba(249, 115, 115, 0.1)",
            borderRadius: "50%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto 1.5rem auto",
            border: "1px solid rgba(249, 115, 115, 0.25)",
            color: "#f97373",
          }}
        >
          <FiLock style={{ fontSize: "2.2rem" }} />
        </div>

        <h1
          style={{
            fontFamily: "Avenir, sans-serif",
            fontWeight: 700,
            fontSize: "1.65rem",
            letterSpacing: "0.03em",
            margin: "0 0 0.5rem 0",
            color: "#f97373", // Vibrant, non-generic warning color
          }}
        >
          ACCESS RESTRICTED
        </h1>

        <p
          style={{
            fontSize: "0.95rem",
            lineHeight: "1.5",
            color: "#B2CCEE",
            margin: "0 0 2rem 0",
          }}
        >
          The account <strong style={{ color: "#ffffff" }}>{user?.email || user?.name || "User"}</strong> is not authorized to access the <strong style={{ color: "#22D4F0" }}>{requestedClient}</strong> portal.
        </p>

        {allowedClients && allowedClients.length > 0 ? (
          <div style={{ textAlign: "left", marginBottom: "2.5rem" }}>
            <h3
              style={{
                fontFamily: "Avenir, sans-serif",
                fontSize: "0.8rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#6BA6F1",
                marginBottom: "0.75rem",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
                paddingBottom: "0.4rem",
              }}
            >
              Authorized Portals
            </h3>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.6rem",
                maxHeight: "160px",
                overflowY: "auto",
                paddingRight: "4px",
              }}
            >
              {allowedClients.map((client) => (
                <a
                  key={client}
                  href={`/${client}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.8rem 1rem",
                    background: "rgba(255, 255, 255, 0.03)",
                    border: "1px solid rgba(255, 255, 255, 0.05)",
                    borderRadius: "10px",
                    color: "#ffffff",
                    textDecoration: "none",
                    fontSize: "0.95rem",
                    transition: "all 0.15s ease-in-out",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "rgba(10, 116, 214, 0.15)";
                    e.currentTarget.style.borderColor = "rgba(10, 116, 214, 0.4)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.03)";
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.05)";
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{client}</span>
                  <FiArrowRight style={{ color: "#22D4F0" }} />
                </a>
              ))}
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: "1rem",
              background: "rgba(255, 255, 255, 0.02)",
              borderRadius: "12px",
              border: "1px solid rgba(255, 255, 255, 0.05)",
              fontSize: "0.9rem",
              color: "#B2CCEE",
              marginBottom: "2.5rem",
              lineHeight: "1.5",
            }}
          >
            The account does not have access to any standard client portals yet.
            Please contact administrator to assign access privileges.
          </div>
        )}

        <button
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          style={{
            width: "100%",
            padding: "0.9rem",
            background: "transparent",
            border: "1px solid rgba(255, 255, 255, 0.15)",
            borderRadius: "10px",
            color: "#e5e7eb",
            fontSize: "0.95rem",
            cursor: "pointer",
            transition: "all 0.15s ease-in-out",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "0.5rem",
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.borderColor = "#f97373";
            e.currentTarget.style.color = "#f97373";
            e.currentTarget.style.background = "rgba(249, 115, 115, 0.05)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)";
            e.currentTarget.style.color = "#e5e7eb";
            e.currentTarget.style.background = "transparent";
          }}
        >
          <FiLogOut />
          Sign Out / Switch Account
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4) LOG OUT HEADER BUTTON
// ---------------------------------------------------------------------------
export function LogOutButton({ user, logout }) {
  return (
    <button
      onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
      style={{
        background: "rgba(249, 115, 115, 0.1)",
        border: "1px solid rgba(249, 115, 115, 0.2)",
        borderRadius: "8px",
        width: "36px",
        height: "36px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: "#f97373",
        cursor: "pointer",
        transition: "all 0.15s ease-in-out",
        marginLeft: "auto",
        flexShrink: 0,
      }}
      title={`Sign Out (${user?.email || user?.name || "Account"})`}
      onMouseOver={(e) => {
        e.currentTarget.style.background = "rgba(249, 115, 115, 0.25)";
        e.currentTarget.style.borderColor = "rgba(249, 115, 115, 0.5)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = "rgba(249, 115, 115, 0.1)";
        e.currentTarget.style.borderColor = "rgba(249, 115, 115, 0.2)";
      }}
    >
      <FiLogOut style={{ fontSize: "1.05rem" }} />
    </button>
  );
}

